import { Text } from '@dixa/lib-shared-habla';
import { Message, MessageEmailRecipient } from 'lib-shared-legacy-types';

type Row = { label: string; list: MessageEmailRecipient[] };

const formatRecipient = ({ email, name }: MessageEmailRecipient): string =>
  name ? `${name} <${email}>` : email;

const getToList = ({ to, to_list_v2 }: Message): MessageEmailRecipient[] =>
  to_list_v2 ?? (to ? [{ email: to }] : []);

/** Whether the message carries any email recipients worth showing. */
export const hasEmailRecipients = (message: Message): boolean =>
  Boolean(
    getToList(message).length ||
    message.cc_list_v2?.length ||
    message.bcc_list_v2?.length,
  );

type ConversationLegacyPreviewRecipientsProps = {
  message: Message;
};

/**
 * Per-message email recipients (From/To/Cc/Bcc). The addressing can differ from
 * message to message, so it's shown on each email message in the printed
 * transcript. Renders nothing for non-email messages.
 */
export const ConversationLegacyPreviewRecipients = ({
  message,
}: ConversationLegacyPreviewRecipientsProps): JSX.Element | null => {
  const { from, cc_list_v2, bcc_list_v2 } = message;

  const toList = getToList(message);
  const ccList = cc_list_v2 ?? [];
  const bccList = bcc_list_v2 ?? [];

  if (!hasEmailRecipients(message)) {
    return null;
  }

  const rows: Row[] = [];
  if (from) {
    rows.push({ label: 'From', list: [{ email: from }] });
  }
  if (toList.length) {
    rows.push({ label: 'To', list: toList });
  }
  if (ccList.length) {
    rows.push({ label: 'Cc', list: ccList });
  }
  if (bccList.length) {
    rows.push({ label: 'Bcc', list: bccList });
  }

  return (
    <div>
      {rows.map(({ label, list }) => (
        <Text.Paragraph key={label} variant="4" subdued as="div">
          <Text.Paragraph as="span" variant="3" subdued>
            {label}:
          </Text.Paragraph>{' '}
          {list.map(formatRecipient).join(', ')}
        </Text.Paragraph>
      ))}
    </div>
  );
};
